import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { apiUrl } from '../config/api';

const PAYMENT_CONTEXT_API = apiUrl('credit_payment_public_context');
const PAYMENT_CHECKOUT_API = apiUrl('credit_payment_public_checkout');
const PAYMENT_STATUS_API = apiUrl('credit_payment_public_status');

function fmt(value) {
  return Number(value || 0).toFixed(2);
}

function statusColor(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'paid') return 'success';
  if (normalized === 'cancelled') return 'warning';
  if (normalized === 'failed') return 'error';
  return 'default';
}

function submitCheckoutForm(actionUrl, fields) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = String(actionUrl || '');
  form.style.display = 'none';

  Object.entries(fields || {}).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = String(value ?? '');
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

export default function CreditPaymentPage() {
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get('token') || '').trim();
  const orderId = String(searchParams.get('order_id') || '').trim();
  const pageState = String(searchParams.get('state') || '').trim().toLowerCase();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const maxPayable = Number(context?.summary?.max_payable || 0);
  const suggestedAmount = Number(context?.summary?.suggested_amount || 0);
  const amountNum = Number(amount || 0);

  const canPay = token && maxPayable > 0 && amountNum > 0 && (amountNum - maxPayable) <= 0.01;

  const statusMessage = useMemo(() => {
    if (!paymentStatus?.status) return '';
    const s = String(paymentStatus.status).toLowerCase();
    if (s === 'paid') return 'Payment completed successfully.';
    if (s === 'cancelled') return 'Payment was cancelled.';
    if (s === 'failed') return 'Payment failed. Please try again.';
    return 'Payment is being processed. Please wait a moment.';
  }, [paymentStatus]);

  useEffect(() => {
    let mounted = true;
    if (!token) {
      setLoading(false);
      setError('Invalid payment link');
      return () => {};
    }

    setLoading(true);
    setError('');

    fetch(`${PAYMENT_CONTEXT_API}?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!mounted) return;
        if (d?.status !== 'ok') {
          setError(d?.message || 'Failed to load payment page');
          setContext(null);
          return;
        }
        setContext(d);
        const defaultAmount = Number(d?.summary?.suggested_amount || d?.summary?.max_payable || 0);
        setAmount(defaultAmount > 0 ? fmt(defaultAmount) : '');
      })
      .catch(() => {
        if (!mounted) return;
        setError('Network error while loading payment page');
        setContext(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    let mounted = true;
    if (!token || !orderId) return () => {};

    const loadStatus = () => {
      setStatusLoading(true);
      fetch(`${PAYMENT_STATUS_API}?token=${encodeURIComponent(token)}&order_id=${encodeURIComponent(orderId)}`)
        .then((r) => r.json())
        .then((d) => {
          if (!mounted) return;
          if (d?.status === 'ok') {
            setPaymentStatus(d.payment || null);
          }
        })
        .catch(() => {
          // ignore poll errors for customer-facing page
        })
        .finally(() => {
          if (mounted) setStatusLoading(false);
        });
    };

    loadStatus();
    const interval = setInterval(loadStatus, 3000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [token, orderId]);

  const handlePay = async () => {
    if (!canPay) return;
    setBusy(true);
    setError('');
    try {
      const d = await fetch(PAYMENT_CHECKOUT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          amount: Number(amountNum.toFixed(2)),
        }),
      }).then((r) => r.json());

      if (d?.status !== 'ok' || !d?.checkout_url || !d?.fields) {
        setError(d?.message || 'Failed to start online payment');
        return;
      }

      submitCheckoutForm(d.checkout_url, d.fields);
    } catch {
      setError('Network error while starting payment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: { xs: 2, md: 4 },
        background: 'linear-gradient(180deg, #f6f8fb 0%, #e8eef7 100%)',
      }}
    >
      <Paper
        sx={{
          maxWidth: 760,
          mx: 'auto',
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          boxShadow: '0 18px 40px rgba(15,23,42,0.10)',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Credit Payment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pay your pending credit balance securely using PayHere.
        </Typography>

        {pageState === 'cancel' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Payment was cancelled. You can try again below.
          </Alert>
        ) : null}

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && context ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {context?.customer?.name || 'Customer'}
              </Typography>
              <Chip
                label={context?.branch?.name || 'Branch'}
                size="small"
                variant="outlined"
              />
            </Stack>

            {context?.sale ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2">
                  Invoice: <b>{String(context.sale.order_details || 'Invoice').trim() || 'Invoice'}</b>
                </Typography>
                <Typography variant="body2">
                  Invoice Amount: <b>Rs. {fmt(context.sale.amount)}</b>
                </Typography>
              </Paper>
            ) : null}

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Typography variant="body2">Open Credit Total</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Rs. {fmt(context?.summary?.open_credit_total)}
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Typography variant="body2">Received So Far</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Rs. {fmt(context?.summary?.open_settlement_total)}
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Pending Balance</Typography>
                <Typography variant="body1" sx={{ fontWeight: 900 }}>
                  Rs. {fmt(context?.summary?.open_balance)}
                </Typography>
              </Stack>
            </Paper>

            <TextField
              label="Payment Amount (Rs.)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              fullWidth
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                disabled={busy || maxPayable <= 0}
                onClick={() => setAmount(fmt(Math.max(0, Math.min(maxPayable, suggestedAmount > 0 ? suggestedAmount / 2 : maxPayable / 2))))}
              >
                Partial Payment
              </Button>
              <Button
                variant="outlined"
                disabled={busy || maxPayable <= 0}
                onClick={() => setAmount(fmt(maxPayable))}
              >
                Full Balance
              </Button>
            </Stack>

            <Button
              variant="contained"
              size="large"
              disabled={busy || !canPay}
              onClick={handlePay}
            >
              {busy ? 'Redirecting...' : 'Pay With PayHere'}
            </Button>

            {amountNum > maxPayable + 0.01 ? (
              <Alert severity="warning">
                Amount cannot exceed pending balance Rs. {fmt(maxPayable)}.
              </Alert>
            ) : null}

            {paymentStatus ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                  <Typography variant="subtitle2">Latest Payment Status</Typography>
                  <Chip
                    size="small"
                    color={statusColor(paymentStatus.status)}
                    label={String(paymentStatus.status || 'created').toUpperCase()}
                  />
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {statusMessage}
                </Typography>
                {statusLoading ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Checking status...
                  </Typography>
                ) : null}
              </Paper>
            ) : null}
          </Stack>
        ) : null}
      </Paper>
    </Box>
  );
}
